import './styles.scss';

import { LostAndFoundHistoryFormDefinitions, LostAndFoundIssueWithDocuments } from '../LostAndFoundHistoryForm/types';
import { useEffect, useMemo } from 'react';
import useSession, { SessionUser } from '@/state/session';

import LostAndFoundHistoryForm from '@/features/LostAndFoundHistory/components/LostAndFoundHistoryForm';
import Popup from '@/common/Popup';
import { PopupTypes } from '@/common/Popup/types';
import { createComponentState } from '@/state';
import { getClasses } from '@/utils';
import useConfirmation from '@/hooks/useConfirmation';
import { useNavigate } from 'react-router-dom';

export type LostAndFoundHistoryDrawerState = {
  show: boolean;
  data: Partial<LostAndFoundIssueWithDocuments>;
};
export const initLostAndFoundHistoryState: LostAndFoundHistoryDrawerState = {
  show: false,
  data: {},
};
export const useLostAndFoundHistoryDrawer = createComponentState<LostAndFoundHistoryDrawerState>(initLostAndFoundHistoryState);
export type LostAndFoundHistoryDrawerProps = { locale?: Record<string, string> } & PopupTypes.Props;

const LostAndFoundHistoryDrawer = ({ locale: customLocale, ...popupProps }: LostAndFoundHistoryDrawerProps): JSX.Element => {
  const [state, setState] = useLostAndFoundHistoryDrawer(({ state, setState }) => [state, setState]);
  const { show, data } = state;
  const user = useSession(({ state }) => state.user || ({} as SessionUser));
  const navigate = useNavigate();
  const confirmHide = useConfirmation({
    Body: {
      message: (
        <div className="IssueConfirmation">
          <h5>Are you sure?</h5>
          <span>Any unsaved changes will be lost.</span>
        </div>
      ),
    },
  });

  const onHide = async (redirect: boolean = false): Promise<void> => {
    try {
      if (redirect) {
        LostAndFoundHistoryFormDefinitions.clearForm();
        setState({ ...state, show: false });
      } else {
        const issueValue = LostAndFoundHistoryFormDefinitions.loadForm();
        const issuesHasValue = Object.keys(issueValue).length > 0;
        if (issuesHasValue) await confirmHide();
        setState({ ...state, show: false });
      }
    } catch (err) {
      console.error('Error hiding drawer:', err);
    }
  };
  const onExited = (): void => setState(initLostAndFoundHistoryState);

  const onSubmit = (data): void => {
    debug.log('lost-and-found-history,lost-and-found-history.drawer', 'Submit Lost and Found', data);
    LostAndFoundHistoryFormDefinitions.clearForm();
    onHide();
  };

  const gotoHistory = (): void => {
    onHide(true);
    navigate('/lost-and-found-history');
  };

  const actions = useMemo(() => {
    const result = [];
    if (!location.pathname.startsWith('/lost-and-found-history'))
      result.push({
        key: 'history',
        label: 'See History',
        onClick: gotoHistory,
      });
    return result.length > 0 ? result : undefined;
  }, [location.pathname, gotoHistory]);

  const value = useMemo(() => {
    if (!show) return undefined;
    const isDispatcher = user?.role?.name === 'dispatch';
    const dispatcherId = isDispatcher && !data?.dispatcherId ? user?.id : data?.dispatcherId;
    const result = { ...data, dispatcherId };
    debug.log('lost-and-found-history,lost-and-found-history.drawer', 'Default Value', result);
    return result;
  }, [show, data, user]);

  useEffect((): void => {
    if (show) return;
    LostAndFoundHistoryFormDefinitions.clearForm();
  }, [show]);

  return (
    <Popup
      {...popupProps}
      className={getClasses('LostAndFoundHistoryDrawer', popupProps.className)}
      show={show}
      onHide={onHide}
      onExited={onExited}
      actions={actions}
    >
      <LostAndFoundHistoryForm onSubmit={onSubmit} value={value} key={data?.id} />
    </Popup>
  );
};

export default LostAndFoundHistoryDrawer;
